import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { spacing, fontStyles, colors, fontFamilies, borderRadius } from '../../styles';
import * as storage from '../../storage';
import * as stores from '../../stores';
import Button from '../Button';
import { getPortfolioReturn, PortfolioReturnResponseItem } from '../../apis/backend';
import ColoredPercentChange from '../ColoredPercentChange';

interface PortfoliosSectionProps {
  portfolios: storage.Portfolio[];
  selectedInterval: '1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' | '5y';
}

const PortfoliosSection: React.FC<PortfoliosSectionProps> = ({ portfolios, selectedInterval }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isRestricted = !purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;
  const hasSeenIntro = stores.General.useState((s) => s.hasSeenPortfoliosSectionIntro);

  const [portfolioSummaries, setPortfolioSummaries] = useState<Record<string, PortfolioReturnResponseItem>>({});

  // Load portfolio summaries
  const loadPortfolioSummaries = useCallback(async () => {
    if (portfolios.length === 0) {
      setPortfolioSummaries({});
      return;
    }
    try {
      // Check and fix portfolios with empty symbols but valid allocations
      const updatedPortfolios = await Promise.all(
        portfolios.map(async (p) => {
          if ((p.symbols?.length ?? 0) === 0 && p.allocations && p.allocations.length > 0) {
            const derivedSymbols = p.allocations.map((a) => a.symbol);
            const updatedPortfolio = { ...p, symbols: derivedSymbols };
            await storage.updatePortfolio(updatedPortfolio);
            return updatedPortfolio;
          }
          return p;
        }),
      );

      const payload = updatedPortfolios.map((p) => ({
        id: p.id,
        symbols: p.symbols || [],
        allocations: p.allocations
          ? p.allocations.map((a) => {
              if (typeof a.allocation_percentage === 'number') {
                return a.allocation_percentage;
              }
              const parsed = parseFloat(a.allocation_percentage as any);
              return isNaN(parsed) ? 0 : parsed / 100;
            })
          : [],
        strategy: (p as any).strategy,
        benchmark: (p as any).benchmark,
      }));

      const summaries = await getPortfolioReturn(payload);

      setPortfolioSummaries(
        summaries.reduce((acc: Record<string, PortfolioReturnResponseItem>, p: PortfolioReturnResponseItem) => {
          acc[p.id] = p;
          return acc;
        }, {}),
      );
    } catch (error) {
      console.error('Error loading portfolio summaries', error);
    }
  }, [portfolios]);

  // Load summaries when portfolios or interval changes
  useEffect(() => {
    loadPortfolioSummaries();
  }, [loadPortfolioSummaries, selectedInterval]);

  const handleDismissHelper = () => {
    stores.General.update((s) => {
      s.hasSeenPortfoliosSectionIntro = true;
    });
  };

  const portfolioItemStyles = css`
    display: flex;
    align-items: center;
    width: 100%;
    padding: ${spacing.deci} ${spacing.micro};
    cursor: pointer;
    border-radius: 8px;
    margin-bottom: ${spacing.micro};
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--hover-background);
    }
  `;

  const portfolioTitleStyles = css`
    ${fontStyles.fatCat};
    color: var(--text-color);
    display: inline-block;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  `;

  const symbolsInfoStyles = css`
    font-size: 0.85em;
    color: var(--secondary-text);
    font-weight: normal;
    white-space: nowrap;
    line-height: 1.2;
    margin-left: 4px;
    flex-shrink: 0;

    @media (max-width: 359px) {
      font-size: 0.8em;
    }
  `;

  const topContributorStyles = css`
    display: flex;
    gap: ${spacing.micro};
    flex-wrap: wrap;
    margin-top: ${spacing.micro};
  `;

  const contributorTagStyles = css`
    padding: ${spacing.micro} ${spacing.centi};
    border-radius: 4px;
    font-weight: 500;
    ${fontStyles.mouse};
  `;

  const topContributorTagStyles = css`
    ${contributorTagStyles};
    background-color: ${colors.leafAir};
    color: ${colors.leaf};

    html[data-theme='dark'] & {
      background-color: transparent;
      border: 1px solid ${colors.leaf};
      padding: calc(${spacing.micro} - 1px) calc(${spacing.centi} - 1px);
    }
  `;

  const topDetractorTagStyles = css`
    ${contributorTagStyles};
    background-color: ${colors.berryAir};
    color: ${colors.berry};

    html[data-theme='dark'] & {
      background-color: transparent;
      border: 1px solid ${colors.berry};
      padding: calc(${spacing.micro} - 1px) calc(${spacing.centi} - 1px);
    }
  `;

  const emptyStateStyles = css`
    text-align: center;
    padding: ${spacing.giga} ${spacing.deci};
    color: var(--secondary-text);
    ${fontStyles.dog};
    background-color: var(--card-background);
    border-radius: 8px;
    border: 1px solid var(--border-color);
    margin: ${spacing.centi} 0;
  `;

  const helperMessageStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${spacing.deci};
    margin-bottom: ${spacing.centi};
    background-color: var(--card-background);
    border-radius: 8px;
    color: var(--text-color);
    ${fontStyles.cat};
  `;

  const helperContentStyles = css`
    display: flex;
    align-items: center;
    gap: ${spacing.centi};
    flex-grow: 1;
  `;

  const helperActionsStyles = css`
    display: flex;
    align-items: center;
    gap: ${spacing.centi};
    flex-shrink: 0;
  `;

  const dismissButtonStyles = css`
    background: none;
    border: none;
    cursor: pointer;
    padding: ${spacing.micro};
    border-radius: 4px;
    color: var(--secondary-text);
    transition: background-color 0.2s ease;
    font-size: 1.5em;

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }

    html[data-theme='dark'] &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  `;

  const addPortfolioStyles = css`
    display: block;
    margin-top: ${spacing.centi};
  `;

  return (
    <div>
      {/* Show helper message when user has exactly 1 portfolio and hasnt seen intro */}
      {portfolios.length === 1 && !hasSeenIntro && (
        <div css={helperMessageStyles}>
          <div css={helperContentStyles}>
            <span>👇🏼</span>
            <span>{t('portfolios.section.tapToAdd')}</span>
          </div>
          <div css={helperActionsStyles}>
            <button
              onClick={handleDismissHelper}
              css={dismissButtonStyles}
              aria-label={t('portfolios.section.dismissHelper')}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {portfolios.length === 0 ? (
        <div css={emptyStateStyles}>{t('portfolios.section.noPortfolios')}</div>
      ) : (
        portfolios.map((portfolio) => {
          const summary = portfolioSummaries[portfolio.id]?.intervals[selectedInterval];
          return (
            <div
              key={portfolio.id}
              css={portfolioItemStyles}
              onClick={() => history.push(`/portfolios/view/${portfolio.id}`)}
              role="button"
              tabIndex={0}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${spacing.milli};
                  flex-grow: 1;
                  min-width: 0;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: baseline;
                    gap: ${spacing.micro};
                  `}
                >
                  <div css={portfolioTitleStyles}>{portfolio.title}</div>
                  <div css={symbolsInfoStyles}>({portfolio.symbols?.length || portfolio.allocations?.length || 0})</div>
                </div>
                {(summary?.top_contributor || summary?.top_detractor) && (
                  <div css={topContributorStyles}>
                    {summary?.top_contributor && (
                      <span css={topContributorTagStyles}>{summary.top_contributor.symbol}</span>
                    )}
                    {summary?.top_detractor && <span css={topDetractorTagStyles}>{summary.top_detractor.symbol}</span>}
                  </div>
                )}
              </div>
              {summary?.change_percent !== undefined && (
                <div
                  css={css`
                    margin-left: ${spacing.deci};
                    align-self: center;
                    white-space: nowrap;
                    flex-shrink: 0;
                  `}
                >
                  <ColoredPercentChange
                    value={summary.change_percent}
                    css={css`
                      padding: ${spacing.centi} ${spacing.deci};
                      border-radius: 8px;
                    `}
                  />
                </div>
              )}
            </div>
          );
        })
      )}

      {!isRestricted || portfolios.length < 2 ? (
        <Link to="/portfolios/add" css={addPortfolioStyles}>
          <Button
            variant="primary"
            block
            css={css`
              padding: ${spacing.centi};
              font-weight: 500;
              border-radius: 8px;
              transition: all 0.2s ease;
            `}
          >
            {t('portfolios.section.addPortfolio')}
          </Button>
        </Link>
      ) : (
        <div css={addPortfolioStyles}>
          <Button
            variant="primary"
            block
            onClick={() => {
              toast.info(
                () => (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      font-family: ${fontFamilies.helveticaNowText};
                      color: ${colors.liquorice};
                      background-color: ${colors.sugar};
                    `}
                  >
                    <span>{t('portfolios.section.proFeature')}</span>
                    <button
                      css={css`
                        margin-left: ${spacing.deci};
                        background: transparent;
                        border: 1px solid ${colors.petrolBlue};
                        color: ${colors.petrolBlue};
                        border-radius: ${borderRadius};
                        padding: ${spacing.milli} ${spacing.centi};
                        cursor: pointer;
                        font-family: ${fontFamilies.helveticaNowText};
                        font-weight: 500;
                        transition: background-color 0.2s ease;

                        &:hover {
                          background-color: rgba(23, 110, 193, 0.1);
                        }

                        html[data-theme='dark'] & {
                          color: ${colors.skyBlue};
                          border-color: ${colors.skyBlue};
                        }
                      `}
                      onClick={() => {
                        toast.dismiss();
                        stores.showPaymentModalByPreference('portfolios_section_subscribe_toast');
                      }}
                    >
                      {t('portfolios.section.subscribe')}
                    </button>
                  </div>
                ),
                {
                  autoClose: 10000,
                  hideProgressBar: false,
                  closeButton: false,
                  closeOnClick: false,
                },
              );
            }}
            css={css`
              padding: ${spacing.centi};
              font-weight: 500;
              border-radius: 8px;
              transition: all 0.2s ease;
            `}
          >
            {t('portfolios.section.addPortfolio')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PortfoliosSection;
